import { FilterTypes } from '../components/TwFiltersList/TwFiltersList';
import { CompaniesEntities } from '../pages/Companies/Companies';
import {
  FACTORING_FILTER_STATUS,
  INVOICE_STATUS,
  QB_DESKTOP_SYNC_STATUS
} from '../providers/Invoices/Invoices.model';
import {
  DRIVER_DEFAULT_PAY_RATE_TYPE,
  DRIVER_EMPLOYMENT_CLASSIFICATION,
  DRIVER_TYPE
} from '../redux/api/driver/driver.model';
import { DRIVER_FILE_STATUS } from '../redux/api/driverFile/driverFile.model';
import { EQUIPMENT_FILE_STATUS } from '../redux/api/equipmentFile/equipmentFile.model';
import { LoadDocumentType, LoadStatus, SegmentStatus } from '../redux/api/load/load.model';
import {
  ORDER_COVERAGE_STATUS,
  ORDER_INVOICE_STATUS,
  ORDER_STATUS
} from '../redux/api/orders/orders.model';
import { SavedViewSection } from '../redux/api/savedViews/savedViews.model';
import { SettlementStatus } from '../redux/api/settlement/settlement.model';
import {
  STOP_TYPE,
  TRIP_DRIVER_PAY_UNIT,
  TRIP_STATUS,
  TripDriver,
  TripDriverAckStatus,
  TripSearchDriverResponse
} from '../redux/api/trips/trips.model';

type EnumObject = { [key: string]: number | string };
type EnumObjectEnum<E extends EnumObject> = E extends {
  [key: string]: infer ET | string;
}
  ? ET
  : never;

class StringUtils {
  static getInitials = (text: string): string => {
    if (!text) return '';
    const words = text?.split(' ');

    if (words.length === 1 && words[0].length === 1) {
      return words[0].charAt(0).toLocaleUpperCase();
    } else if (words.length === 1 && words[0].length > 1) {
      const isnum = /^\d+$/.test(words[0]);
      if (isnum) {
        return words[0].charAt(0).toLocaleUpperCase();
      } else {
        const cleanWord = words[0].replace(/[^a-zA-Z]+/g, '');
        return cleanWord.charAt(0).toLocaleUpperCase();
      }
    } else {
      const isnum = /^(?=.*\d)[\d ]+/.test(text);

      if (isnum) {
        return text[0].charAt(0).toLocaleUpperCase();
      } else {
        const newWords = words
          .slice(0, 2)
          .map((word) => {
            const clean = word.replace(/[^a-zA-Z]+/g, '');

            if (clean !== '') {
              return clean;
            }
          })
          .filter((w) => w !== undefined);

        if (newWords.length > 1) {
          return `${newWords[0]?.charAt(0).toLocaleUpperCase()}${newWords[1]
            ?.charAt(0)
            .toLocaleUpperCase()}`;
        } else {
          return `${newWords[0]?.charAt(0).toLocaleUpperCase()}`;
        }
      }
    }
  };

  static getStatusLabel = (status?: ORDER_STATUS): string => {
    switch (status) {
      case ORDER_STATUS.CANCELLED:
        return 'Cancelled';
      case ORDER_STATUS.NOT_STARTED:
        return 'Not started';
      case ORDER_STATUS.IN_TRANSIT:
        return 'In Transit';
      case ORDER_STATUS.DELIVERED:
        return 'Delivered';
      default:
        return 'Not started';
    }
  };

  static getOrderStatusLabel = (status: ORDER_STATUS): string => {
    switch (status) {
      case ORDER_STATUS.CANCELLED:
        return 'Cancelled';
      case ORDER_STATUS.NOT_STARTED:
        return 'Not started';
      case ORDER_STATUS.IN_TRANSIT:
        return 'In Transit';
      case ORDER_STATUS.DELIVERED:
        return 'Delivered';
      default:
        return 'Not started';
    }
  };

  static getInvoiceStatusLabel = (status?: INVOICE_STATUS): string => {
    switch (status) {
      case INVOICE_STATUS.created:
        return 'Created';
      case INVOICE_STATUS.sent:
        return 'Sent';
      case INVOICE_STATUS.paid:
        return 'Paid';
      case INVOICE_STATUS.overdue:
        return 'Overdue';
      case INVOICE_STATUS.cancelled:
        return 'Cancelled';
      default:
        return 'Created';
    }
  };

  static getFactoringStatusLabel = (status?: FACTORING_FILTER_STATUS): string => {
    switch (status) {
      case FACTORING_FILTER_STATUS.notSent:
        return 'Not sent';
      case FACTORING_FILTER_STATUS.sentToFactoring:
        return 'Sent to factoring';
      case FACTORING_FILTER_STATUS.error:
        return 'Error';
      default:
        return '';
    }
  };

  static getQbDesktopSyncStatusLabel = (status?: QB_DESKTOP_SYNC_STATUS): string => {
    switch (status) {
      case QB_DESKTOP_SYNC_STATUS.ERROR:
        return 'Error';
      case QB_DESKTOP_SYNC_STATUS.SYNCED:
        return 'Synced';
      case QB_DESKTOP_SYNC_STATUS.NOT_SYNCED:
        return 'Not synced';
      default:
        return '';
    }
  };

  static getOrderInvoiceStatusLabel = (status?: ORDER_INVOICE_STATUS): string => {
    switch (status) {
      case ORDER_INVOICE_STATUS.invoiceCreated:
        return 'Invoice Created';
      case ORDER_INVOICE_STATUS.invoiceSent:
        return 'Invoice Sent';
      case ORDER_INVOICE_STATUS.paid:
        return 'Paid';
      case ORDER_INVOICE_STATUS.readyForInvoicing:
        return 'Ready for invoicing';
      case ORDER_INVOICE_STATUS.notInvoiced:
        return 'Not invoiced';
      case ORDER_INVOICE_STATUS.invoicedManually:
        return 'Invoiced manually';
      default:
        return 'Not invoiced';
    }
  };

  static getSegmentStatusLabel = (status?: TRIP_STATUS): string => {
    switch (status) {
      case TRIP_STATUS.CANCELLED:
        return 'Cancelled';
      case TRIP_STATUS.DRAFT:
        return 'Draft';
      case TRIP_STATUS.DISPATCHED:
        return 'Dispatched';
      case TRIP_STATUS.IN_TRANSIT:
        return 'In Transit';
      case TRIP_STATUS.DELIVERED:
        return 'Delivered';
      default:
        return 'Draft';
    }
  };

  /**
   * Returns an array with the keys of an enum
   * @param enumObject for example: LoadStatus
   * @returns An array of enum keys, for example: [draft, inTransit, readyForInvoicing]
   */
  static getEnumValues = <E extends EnumObject>(enumObject: E): EnumObjectEnum<E>[] => {
    return Object.keys(enumObject)
      .filter((key) => Number.isNaN(Number(key)))
      .map((key) => enumObject[key] as EnumObjectEnum<E>);
  };

  static getNextLoadStatus = (status?: LoadStatus): LoadStatus | null => {
    switch (status) {
      case LoadStatus.draft:
        return LoadStatus.dispatched;
      case LoadStatus.dispatched:
        return LoadStatus.inTransit;
      case LoadStatus.inTransit:
        return LoadStatus.delivered;
      case LoadStatus.delivered:
        return LoadStatus.readyForInvoicing;
      case LoadStatus.readyForInvoicing:
        return LoadStatus.invoiced;
      case LoadStatus.invoiced:
        return LoadStatus.paid;
      case LoadStatus.paid:
        return null;
      default:
        return null;
    }
  };

  static getNextTripStatus = (status?: TRIP_STATUS | null): TRIP_STATUS | null => {
    switch (status) {
      case TRIP_STATUS.CANCELLED:
        return TRIP_STATUS.DRAFT;
      case TRIP_STATUS.DRAFT:
        return TRIP_STATUS.DISPATCHED;
      case TRIP_STATUS.DISPATCHED:
        return TRIP_STATUS.IN_TRANSIT;
      case TRIP_STATUS.IN_TRANSIT:
        return TRIP_STATUS.DELIVERED;
      default:
        return null;
    }
  };

  /**
   *  Note this function is for the public link only as we're showing 'Start load'
   *  prompt for both draft and dispatched load status externally
   * */
  static getNextLoadStatusLabelForProgressButtonInPublicLink = (status?: LoadStatus): string => {
    switch (status) {
      case LoadStatus.draft:
      case LoadStatus.dispatched:
        return 'Mark as picked up';
      case LoadStatus.inTransit:
        return 'Mark as delivered';
      case LoadStatus.delivered:
        return 'Confirm payment';
      case LoadStatus.paid:
        return '';
      default:
        return '';
    }
  };

  static getNextSegmentStatusLabelForProgressButtons = (status?: SegmentStatus): string => {
    switch (status) {
      case SegmentStatus.notStarted:
        return 'Mark as picked up';
      case SegmentStatus.inTransit:
        return 'Mark as delivered';
      case SegmentStatus.delivered:
        return '';
      default:
        return '';
    }
  };

  static getLoadDocumentTypeLabel = (type?: LoadDocumentType): string => {
    switch (type) {
      case LoadDocumentType.proofOfDelivery:
        return 'Proof of Delivery';
      case LoadDocumentType.rateConfirmation:
        return 'Rate Confirmation';
      case LoadDocumentType.billOfLading:
        return 'Bill of Lading';
      case LoadDocumentType.lumper:
        return 'Lumper';
      case LoadDocumentType.receipt:
        return 'Receipt';
      case LoadDocumentType.invoice:
        return 'Invoice';
      case LoadDocumentType.tripManifest:
        return 'Trip Manifest';
      case LoadDocumentType.permit:
        return 'Permit';
      case LoadDocumentType.driverInstructions:
        return 'Driver Instructions';
      case LoadDocumentType.other:
        return 'Other';
      case LoadDocumentType.empty:
        return '';
      default:
        return '';
    }
  };

  static copyToClipboard = async (text: string): Promise<void | boolean> => {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      // For older browsers.
      return document.execCommand('copy', true, text);
    }
  };

  static capitalizeFirstLetter = (text: string): string =>
    text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

  static getLoadIntermediateStopLabel = (stopType?: STOP_TYPE): string => {
    switch (stopType) {
      case STOP_TYPE.PICKUP:
        return 'Pickup';
      case STOP_TYPE.DROP_OFF:
        return 'Drop';
      case STOP_TYPE.TRANSFER:
        return 'Transfer';
      default:
        return 'Pickup';
    }
  };

  static getDriverClassificationLabel = (
    classification?: DRIVER_EMPLOYMENT_CLASSIFICATION
  ): string => {
    switch (classification) {
      case DRIVER_EMPLOYMENT_CLASSIFICATION.CONTRACTOR:
        return 'Contractor';
      case DRIVER_EMPLOYMENT_CLASSIFICATION.EMPLOYEE:
        return 'Employee';
      default:
        return 'Contractor';
    }
  };

  static getDriverTypeLabel = (type?: DRIVER_TYPE): string => {
    switch (type) {
      case DRIVER_TYPE.OWNER_OPERATOR_DRIVER:
        return 'Owner operator driver';
      case DRIVER_TYPE.COMPANY_DRIVER:
        return 'Company driver';
      default:
        return 'Company driver';
    }
  };

  static getDriverPayRateTypeLabel = (
    payRateType?: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT
  ): string => {
    switch (payRateType) {
      case DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE:
      case TRIP_DRIVER_PAY_UNIT.PERCENTAGE:
        return 'Percentage-based';
      case DRIVER_DEFAULT_PAY_RATE_TYPE.PER_HOUR:
      case TRIP_DRIVER_PAY_UNIT.PER_HOUR:
        return 'Per-hour';
      case DRIVER_DEFAULT_PAY_RATE_TYPE.PER_MILE:
      case TRIP_DRIVER_PAY_UNIT.PER_MILE:
        return 'Per-mile';
      case DRIVER_DEFAULT_PAY_RATE_TYPE.FLAT:
        return 'Flat (per trip)';
      default:
        return 'Percentage-based';
    }
  };

  /**
   * Short driver pay rate formatted
   * @param rate
   * @param type
   * @returns $10/mi, $10/hr or 10%
   */
  static getDriverFormattedPayRate = (
    rate: string,
    payRateType: DRIVER_DEFAULT_PAY_RATE_TYPE | TRIP_DRIVER_PAY_UNIT
  ): string => {
    if (!rate) return '';

    const cleanRate = rate.replace(/(\%|\$)/gi, '');

    switch (payRateType) {
      case DRIVER_DEFAULT_PAY_RATE_TYPE.PERCENTAGE:
      case TRIP_DRIVER_PAY_UNIT.PERCENTAGE:
        return `${cleanRate}%`;
      case DRIVER_DEFAULT_PAY_RATE_TYPE.PER_HOUR:
      case TRIP_DRIVER_PAY_UNIT.PER_HOUR:
        return `$${cleanRate}/hr`;
      case DRIVER_DEFAULT_PAY_RATE_TYPE.PER_MILE:
      case TRIP_DRIVER_PAY_UNIT.PER_MILE:
        return `$${cleanRate}/mi`;
      default:
        return `$${rate}`;
    }
  };

  static formatPhoneNumber = (phoneNumber: string, ext: string | undefined = undefined): string => {
    let number = '';
    if (phoneNumber.length > 10) {
      number = `(${phoneNumber.substring(1, 4)}) ${phoneNumber.substring(
        4,
        7
      )} - ${phoneNumber.substring(7)} `;
    } else if (phoneNumber !== '') {
      number = `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
        3,
        6
      )} - ${phoneNumber.substring(6)}`;
    }

    if (ext) {
      number = number + ' Ext. ' + ext;
    }

    return number;
  };

  /**
   * Returns true if email has valid format
   * @param email
   * @returns
   */
  static isValidEmail = (email: string): boolean => {
    const format = /^[\w-+\.]+@([\w-]+\.)+[\w-]{2,}$/;
    return format.test(email);
  };

  /**
   * @param value string
   * @returns Sanitized string with no blank spaces
   */
  static removeBlankSpacesFrom = (value: string): string => {
    return value.replace(/\s+/g, '');
  };

  /**
   * @param value string
   * @returns replace blank spaces by dash and lower case
   */
  static replaceBlankSpacesWithDash = (value: string): string => {
    return value.replace(/ /g, '-').toLowerCase();
  };

  /**
   * @param value string
   * @returns replace blank spaces by underscore
   */
  static replaceBlankSpacesWithUnderscore = (value: string): string => {
    return value.replace(/ /g, '_');
  };

  /**
   * Returns truncated string if input is larger than max
   * @param str, n
   * @returns
   */

  static truncateString = (str: string, max: number): string => {
    return str.length > max ? str.substr(0, max) + '...' : str;
  };

  /**
   *  Returns string if original value is not null or undefined or empty string
   *  @param value
   *  @returns string | undefined
   */
  static toNonEmptyString = (value: string | number | null | undefined): string | undefined => {
    return value && value.toString().trim().length > 0 ? value.toString().trim() : undefined;
  };

  static toReadableOrderCoverageString = (coverage: ORDER_COVERAGE_STATUS): string => {
    switch (coverage) {
      case ORDER_COVERAGE_STATUS.COVERED:
        return 'Covered';
      case ORDER_COVERAGE_STATUS.PARTIALLY_COVERED:
        return 'Partially Covered';
      case ORDER_COVERAGE_STATUS.UNCOVERED:
        return 'Uncovered';
      default:
        return '-';
    }
  };

  static toStatusOrderCoverage = (coverage: string): string => {
    switch (coverage) {
      case 'Covered':
        return ORDER_COVERAGE_STATUS.COVERED;
      case 'Partially Covered':
        return ORDER_COVERAGE_STATUS.PARTIALLY_COVERED;
      case 'Uncovered':
        return ORDER_COVERAGE_STATUS.UNCOVERED;
      default:
        return '-';
    }
  };

  static toReadableFilterName = (filter: FilterTypes, section?: SavedViewSection): string => {
    /* Some excluded filters from the list below as they are not visible in the UI:
    /* 'includeArchived',
    /* 'to,
    /* 'from' */

    switch (filter) {
      case 'customer':
        return 'Customer';
      case 'carrier':
        return 'Carrier';
      case 'coverage':
        if (!section) return 'Coverage';
        if (section === 'ORDERS') return 'Order coverage';
        if (section === 'TRIPS') return 'Trip coverage';

        return 'Coverage';
      case 'customerOrderNumber':
        return 'Customer Order Number';
      case 'deliveryDate':
        return 'Delivery Date';
      case 'deliveryLocation':
        return 'Delivery Location';
      case 'dispatcher':
        return 'Dispatcher';
      case 'driver':
        return 'Driver';
      case 'order':
        return 'Order';
      case 'pickupDate':
        return 'Pickup Date';
      case 'pickupLocation':
        return 'Pickup Location';
      case 'orderStatus':
        return 'Order Status';
      case 'tripStatus':
        return 'Trip Status';
      case 'tags':
        return 'Tags';
      case 'trailer':
        return 'Trailer';
      case 'trip':
        return 'Trip';
      case 'truck':
        return 'Truck';
      // Order specific filters
      case 'invoiceStatus':
        return 'Invoice Status';
      case 'bookingMode':
        return 'Booking Mode';
      case 'division':
        return 'Division';
      case 'settlementStatus':
        return 'Settlement Status';
      case 'manifestType':
        return 'Border-crossing manifest';
      case 'nextStopLocation':
        return 'Next stop location';
      case 'onTimeStatus':
        return 'On-time status';
      case 'missingDocs':
        return 'Missing documents';
      case 'dispatchAcceptance':
        return 'Dispatch acceptance';
      case 'commodityIds':
        return 'Commodity';
      default:
        return '';
    }
  };

  static getDriverFileStatusLabel = (status?: DRIVER_FILE_STATUS): string => {
    switch (status) {
      case DRIVER_FILE_STATUS.ACTIVE:
        return 'Good standing';
      case DRIVER_FILE_STATUS.EXPIRING_WITHIN_30_DAYS:
        return 'Expiring soon';
      case DRIVER_FILE_STATUS.EXPIRED:
        return 'Expired';
      default:
        return 'Good standing';
    }
  };

  static getEquipmentFileStatusLabel = (status?: EQUIPMENT_FILE_STATUS): string => {
    switch (status) {
      case EQUIPMENT_FILE_STATUS.ACTIVE:
        return 'Good standing';
      case EQUIPMENT_FILE_STATUS.EXPIRING_WITHIN_30_DAYS:
        return 'Expiring soon';
      case EQUIPMENT_FILE_STATUS.EXPIRED:
        return 'Expired';
      default:
        return 'Good standing';
    }
  };

  static showSettlementStatusLabel = (settlementStatus: SettlementStatus): string => {
    switch (settlementStatus) {
      case SettlementStatus.IN_PROGRESS:
        return 'Draft';
      case SettlementStatus.PAID:
        return 'Paid';

      case SettlementStatus.DRAFT:
        return 'Not started';
      default:
        return 'Not started';
    }
  };

  static isUuid = (value: string): boolean => {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      value
    );
  };

  static getCompanyEntityLabel = (entity: CompaniesEntities): string => {
    switch (entity) {
      case CompaniesEntities.ownerOperator:
        return 'Owner operator';
      case CompaniesEntities.division:
        return 'Division';
      case CompaniesEntities.customer:
        return 'Customer';
      case CompaniesEntities.carrier:
        return 'Carrier';
      default:
        return '';
    }
  };

  static getTripDispatchAcceptanceLabel = (status?: TripDriverAckStatus | null): string => {
    if (!status) return '';

    switch (status) {
      case TripDriverAckStatus.SENT:
        return 'Sent';
      case TripDriverAckStatus.CONFIRMED:
        return 'Confirmed';
      case TripDriverAckStatus.ACCEPTED:
        return 'Accepted';
      case TripDriverAckStatus.REJECTED:
        return 'Rejected';
      default:
        return 'Sent';
    }
  };

  static getTripDispatchAcceptanceColumnLabel = (
    drivers: TripSearchDriverResponse[],
    status?: TripDriverAckStatus | null
  ): string => {
    if (!status) return '';

    const driversTotal = drivers.length;

    const getDriverCounter = (status: TripDriverAckStatus): number => {
      let driversCounter = 0;
      drivers.forEach((driver) => {
        if (driver.driver_ack_status === status) {
          driversCounter++;
        }
      });

      return driversCounter;
    };

    switch (status) {
      case TripDriverAckStatus.SENT:
        return 'Sent';
      case TripDriverAckStatus.CONFIRMED: {
        if (driversTotal === 1) return 'Confirmed';

        const driversCounter = getDriverCounter(TripDriverAckStatus.CONFIRMED);
        return `Confirmed ${driversCounter} of ${driversTotal}`;
      }
      case TripDriverAckStatus.ACCEPTED: {
        if (driversTotal === 1) return 'Accepted';

        const driversCounter = getDriverCounter(TripDriverAckStatus.ACCEPTED);
        return `Accepted ${driversCounter} of ${driversTotal}`;
      }
      case TripDriverAckStatus.REJECTED: {
        if (driversTotal === 1) return 'Rejected';

        const driversCounter = getDriverCounter(TripDriverAckStatus.REJECTED);
        return `Rejected ${driversCounter} of ${driversTotal}`;
      }
      default:
        return 'Sent';
    }
  };

  static getTripDispatchAcceptanceStatusLabel = (
    drivers: TripSearchDriverResponse[] | TripDriver[]
  ): string[] => {
    const driverLabels: string[] = [];
    drivers.forEach((driver) => {
      const name = 'driver' in driver ? driver.driver.name : driver.name;
      if (driver.driver_ack_status === TripDriverAckStatus.SENT) {
        driverLabels.push(`${name} - Sent`);
      }

      if (driver.driver_ack_status === TripDriverAckStatus.REJECTED) {
        driverLabels.push(`${name} - Rejected`);
      }

      if (driver.driver_ack_status === TripDriverAckStatus.CONFIRMED) {
        driverLabels.push(`${name} - Confirmed`);
      }

      if (driver.driver_ack_status === TripDriverAckStatus.ACCEPTED) {
        driverLabels.push(`${name} - Accepted`);
      }
    });

    return driverLabels;
  };

  static addApostropheToString = (string: string): string => {
    if (!string) return '';

    if (string.endsWith('s')) {
      return string + "'";
    } else {
      return string + "'s";
    }
  };
}

export const toTitleCase = (str: unknown): string => {
  if (typeof str !== 'string') {
    return '';
  }
  return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
};

export default StringUtils;
